import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { REACT_APP_UPLOADS } from '../../../constants';

interface SummaryData {
  totalRecords?: number;
  concludedCases?: number;
  speciesTypes?: number;
  countriesActive?: number;
}

function useCountUp(target: number, duration = 2200, started = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!started || target === 0) return;
    const startTime = Date.now();
    const frame = () => {
      const elapsed  = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased    = 1 - Math.pow(1 - progress, 4);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }, [target, duration, started]);
  return count;
}

const staticStats = [
  { label: 'Conviction Rate',   sublabel: 'for seizures > 500 kg — changing this', staticVal: '<5%' },
  { label: 'Offenders Tracked', sublabel: 'active intelligence profiles',            countTarget: 2000 },
];

export default function StatsBar() {
  const ref   = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [data, setData]       = useState<SummaryData>({ totalRecords: 0, speciesTypes: 0 });

  useEffect(() => {
    axios
      .get(`${REACT_APP_UPLOADS}/api/v1/seizure-cases/summary`)
      .then((res) => { if (res.status === 200) setData(res.data); })
      .catch((_err) => { /* summary unavailable — stats show zeros */ });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.25 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const totalCount    = useCountUp(data.totalRecords    || 0, 2200, visible);
  const speciesCount  = useCountUp(data.speciesTypes    || 0, 2200, visible);
  const countryCount  = useCountUp(data.countriesActive || 0, 1600, visible);
  const offenderCount = useCountUp(2000, 2200, visible);

  const dynamicStats = [
    { label: 'Seizures Reported',  sublabel: 'across all monitored regions',   display: `${totalCount}+` },
    { label: 'Species Monitored',  sublabel: 'from mammals to marine life',     display: `${speciesCount}+` },
    { label: 'Countries',          sublabel: 'active operations tracked',       display: countryCount ? `${countryCount}+` : '—' },
  ];

  return (
    <div
      ref={ref}
      className="w-full bg-[#052e16] py-14 lg:py-16 px-6 lg:px-10 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-y-10 gap-x-4">

          {/* Dynamic stats */}
          {dynamicStats.map((stat, i) => (
            <StatItem
              key={stat.label}
              display={stat.display}
              label={stat.label}
              sublabel={stat.sublabel}
              visible={visible}
              delay={i * 0.1}
              divider={true}
            />
          ))}

          {/* Static stats */}
          {staticStats.map((stat, i) => {
            let display = stat.staticVal ?? '';
            if (!display) {
              if (stat.label === 'Offenders Tracked') display = `${offenderCount.toLocaleString()}+`;
            }
            return (
              <StatItem
                key={stat.label}
                display={display}
                label={stat.label}
                sublabel={stat.sublabel}
                visible={visible}
                delay={(dynamicStats.length + i) * 0.1}
                divider={i < staticStats.length - 1}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatItem({
  display,
  label,
  sublabel,
  visible,
  delay,
  divider,
}: {
  display: string;
  label: string;
  sublabel: string;
  visible: boolean;
  delay: number;
  divider: boolean;
}) {
  return (
    <div
      className={`reveal ${visible ? 'visible' : ''} flex flex-col items-start ${
        divider ? 'md:border-r md:border-white/10 md:pr-6' : ''
      }`}
      style={{ transitionDelay: `${delay}s` }}
    >
      <span
        className="text-3xl lg:text-4xl font-bold mb-1.5 tabular-nums"
        style={{ fontFamily: 'var(--font-display)', color: '#d4a84b' }}
      >
        {display}
      </span>
      <span
        className="text-white text-sm font-semibold leading-tight"
        style={{ fontFamily: 'var(--font-body)' }}
      >
        {label}
      </span>
      <span
        className="text-white/35 text-xs mt-1 leading-snug hidden md:block"
        style={{ fontFamily: 'var(--font-body)' }}
      >
        {sublabel}
      </span>
    </div>
  );
}
